/**
 * Device detection utilities
 * Centralized helpers for responsive behavior and feature detection
 */

/**
 * Check if the current device is a touch-capable device
 * Used for swipe navigation and mobile-specific features
 */
export function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Check if the current viewport is mobile-sized (< 640px)
 * Based on Tailwind's 'sm' breakpoint
 */
export function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 640;
}

/**
 * Check if we're on a mobile device where printing is unreliable
 * Mobile browsers often have issues with iframe-based printing
 */
export function isMobilePrintUnsupported() {
  if (typeof window === 'undefined') return false;
  
  // Check for mobile user agents
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
  
  // Also check viewport width as a fallback
  const isMobileWidth = window.innerWidth < 768;
  
  return isMobileUA || (isTouchDevice() && isMobileWidth);
}

/**
 * Custom hook to track if we're on mobile
 * Re-evaluates on window resize
 */
export function useMobileDetection() {
  // This would need useState and useEffect for React hooks
  // For now, return static check - components can call this on render
  return {
    isMobile: isMobileViewport(),
    isTouch: isTouchDevice(),
    isPrintSupported: !isMobilePrintUnsupported()
  };
}
